// AuthProvider — wraps the app in a Supabase session context.
//
// Exposes { session, user, loading }. Subscribes to auth state changes so
// the entire tree re-renders on sign-in/sign-out without polling. Persists
// the session in localStorage (configured in supabase.ts), so refresh
// doesn't kick the user out.
//
// Read the workspace memberships out of the session in WorkspaceProvider,
// not here — keeping concerns separate makes testing each piece simpler.

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { readWorkspacesFromToken } from "@/lib/jwt";
import { flushPendingLegalAcceptance } from "@/lib/legal-client";

interface AuthState {
  session: Session | null;
  user: User | null;
  loading: boolean;
}

const AuthContext = createContext<AuthState>({
  session: null,
  user: null,
  loading: true,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);

      // If the stored session has no workspaces in the JWT claim, the
      // JWT may have been issued before the auth-hook was wired or
      // before the user joined any workspaces. supabase-js only auto-
      // refreshes near expiry (1hr default), so claims can stay stale.
      if (data.session) {
        const workspaces = readWorkspacesFromToken(data.session.access_token);
        if (workspaces.length === 0) {
          console.log("[auth] JWT has no workspaces claim — forcing refresh");
          supabase.auth.refreshSession().catch((err) => {
            console.warn("[auth] force refresh failed:", err);
          });
        }
      }
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      if (!mounted) return;
      const claimCount = readWorkspacesFromToken(next?.access_token).length;
      console.log(`[auth] state change event=${event} workspaces=${claimCount}`);
      setSession(next);

      // A signup made while email confirmation is on has no session, so the
      // terms acceptance was parked in localStorage. This is the first moment
      // there is an account to write it against. It is a no-op when nothing
      // was parked, and never throws.
      if (next?.user) {
        void flushPendingLegalAcceptance(next.user.email);
      }
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({ session, user: session?.user ?? null, loading }),
    [session, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
